import { IDocumentData } from "../../../../core/_models";
import type { StepTab } from "../../ProjectInitiation-steps/core/_models";

export interface IFeasibilityOption {
    row_id: number | null;
    option_name: string;
    description: string;
    capital_cost: number;
    operating_cost: number;
    delivery_time: number;
    expected_benefits: string;
    strategic_alignment: number;
    complexity: number;
    risk_rating: number;
    score: number;
}

export interface IProjectFeasibilityAndOptionsAnalysis {
    justification_of_option: Record<string, string>;
    formAction?: string;
    iscompleted?: boolean;
}

// One row of the Project Resources table (tbl_project_resources), kept per stage.
// resource_id is 0 / absent for a row not saved yet.
export interface IProjectResource {
    resource_id?: number;
    skill: string;
    no_of_resources: string | number;
    rate_per_hour: string | number;
    notes: string | null;
}

// The five blocks (supplementary infrastructure, boundaries, land, professional services, technology)
// are one project-fields section; the land answers are "yes" or "no".
export interface IProjectScopeResourcesNeed {
    scope_supplementary_infrastructure_narrative: string;
    scope_supplementary_infrastructure_evidence_reference: string;
    scope_boundaries_narrative: string;
    scope_in_scope: string;
    scope_out_of_scope: string;
    scope_boundaries_evidence_reference: string;
    scope_land_property_is_land_identified: string;
    scope_land_property_is_land_ownership_confirmed: string;
    scope_land_property_is_zoning_compliant: string;
    scope_land_property_narrative: string;
    scope_land_property_evidence_reference: string;
    scope_professional_services_narrative: string;
    scope_professional_services_evidence_reference: string;
    scope_technology_equipment_narrative: string;
    scope_technology_equipment_evidence_reference: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IBaseCaseAndCounterfactualScenarioEngine {
    counterfactual_scenario_type: string[];
    current_capacity: number | string;
    base_case_current_demand: number | string;
    current_service_gap: number | string;
    current_cost_of_service: number | string;
    consequences_of_no_intervention: string[];
    base_case_confidence_level: string;
    formAction?: string;
    iscompleted?: boolean;
}

// Field names match the stage 2 fiscal_sustainability_engine_ifisa project-fields section.
export interface IFiscalSustainabilityEngine {
    operating_cost_coverage: number | '';
    lifecycle_cost_coverage: number | '';
    revenue_sufficiency: number | '';
    fiscal_affordability_index: number | '';
    funding_gap_percentage: number | '';
    debt_exposure: number | '';
    contingent_liability_exposure: string;
    formAction?: string;
    iscompleted?: boolean;
}

// Field names match the stage 2 financial_and_funding_strategy_engine project-fields section; each
// tab is one of its sub-sections.
export interface IFinancialAndFundingStrategyEngine {
    funding_sources: string[];
    funding_secured: number | '';
    funding_pending: number | '';
    funding_gap: number | '';
    private_sector_contribution: number | '';
    public_sector_contribution: number | '';
    revenue_model: string;
    innovative_financing_mechanisms: string[];
    innovative_financing_structure_and_application: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface IRiskAndSensitivityAnalysisEngine {
    capex_scenario: number;
    demand_scenario: number;
    inflation_scenario: number;
    revenue_scenario: number;
    schedule_delay_months: number;
    formAction?: string;
    iscompleted?: boolean;
}

// Each SG2/D card is its value (D15 has none), validation notes and an evidence reference.
export interface IDemandAndCapacityValidation {
    dcv_counterfactual_scenario_type: string[];
    dcv_counterfactual_scenario_type_validation_notes: string;
    dcv_counterfactual_scenario_type_evidence_reference: string;
    dcv_current_capacity: number | '';
    dcv_current_capacity_validation_notes: string;
    dcv_current_capacity_evidence_reference: string;
    dcv_current_demand: number | '';
    dcv_current_demand_validation_notes: string;
    dcv_current_demand_evidence_reference: string;
    dcv_current_service_gap: number | '';
    dcv_current_service_gap_validation_notes: string;
    dcv_current_service_gap_evidence_reference: string;
    dcv_current_cost_of_service: number | '';
    dcv_current_cost_of_service_validation_notes: string;
    dcv_current_cost_of_service_evidence_reference: string;
    dcv_consequences_of_no_intervention: string[];
    dcv_consequences_of_no_intervention_validation_notes: string;
    dcv_consequences_of_no_intervention_evidence_reference: string;
    dcv_base_case_confidence_level: string;
    dcv_base_case_confidence_level_validation_notes: string;
    dcv_base_case_confidence_level_evidence_reference: string;
    dcv_current_demand_units_validation_notes: string;
    dcv_current_demand_units_evidence_reference: string;
    dcv_future_demand_horizon_year: number | '';
    dcv_future_demand_horizon_year_validation_notes: string;
    dcv_future_demand_horizon_year_evidence_reference: string;
    dcv_population_served: number | '';
    dcv_population_served_validation_notes: string;
    dcv_population_served_evidence_reference: string;
    dcv_growth_rate: number | '';
    dcv_growth_rate_validation_notes: string;
    dcv_growth_rate_evidence_reference: string;
    dcv_peak_capacity_required: number | '';
    dcv_peak_capacity_required_validation_notes: string;
    dcv_peak_capacity_required_evidence_reference: string;
    dcv_demand_confidence_level: string;
    dcv_demand_confidence_level_validation_notes: string;
    dcv_demand_confidence_level_evidence_reference: string;
    formAction?: string;
    iscompleted?: boolean;
}

// Field names match the stage 2 economies_of_scale_engine project-fields section.
export interface IEconomiesOfScaleAssessment {
    economies_of_scale_assessment: string;
    formAction?: string;
    iscompleted?: boolean;
}

// Field names match the stage 2 special_procurement_actions_and_social_impact project-fields section.
export interface ISpecialProcurementActionsAndSocialImpact {
    gender_responsive_procurement: string;
    social_benefits_value_reference: string;
    sustainability_and_environmental_compliance: string;
    legal_and_technical_due_diligence: string;
    formAction?: string;
    iscompleted?: boolean;
}

// Field names match the stage 2 procurement_strategy project-fields section; each tab is one of its
// sub-sections. Supporting documents upload on their own.
export interface IProcurementStrategy {
    procurement_strategy_type: number;
    supplier_market_maturity: string;
    risk_sharing_mechanism: string[];
    budget_statement: string;
    procurement_procedure: string;
    procurement_plan: string;
    economies_of_scale_summary_of_analysis_findings: string;
    formAction?: string;
    iscompleted?: boolean;
}

// A row of the Partner Agencies table; saved as a stage 2 section item, partner_agency_id being its id.
export interface IPartnerAgency {
    partner_agency_id: number;
    agency_name: string;
    role: string;
    deliverables: string;
}

// Field names match the stage 2 governance_policy_enabling_environment project-fields section; each
// tab is one of its sub-sections. Partner agencies and signed agreements are saved on their own.
export interface IGovernancePolicy {
    government_policies_governance_role: string;
    governance_structure: string;
    internal_controls_accountability: string;
    agreements_dispute_resolution: string;
    incentives_penalties_framework: string;
    national_policy_alignment: string;
    regulatory_compliance_frameworks: string[];
    policy_alignment_summary_bfi_consistency: string;
    formAction?: string;
    iscompleted?: boolean;
}

export interface ICbaCeaIntelligenceEngine {
    capital_cost: number;
    annual_operating_cost: number;
    economic_benefits: number;
    social_benefits: number;
    residual_value: number;
    discount_rate: number;
    analysis_period_years: number;
    cost_per_beneficiary: number;
    cost_per_household: number;
    cost_per_service_unit: number;
    cost_per_job_created: number;
    cost_per_kilometre: number;
    cost_per_outcome_achieved: number;
    bcr: number;
    irr: number;
    npv: number;
    formAction?: string;
    iscompleted?: boolean;
}


export const FINANCIAL_FUNDING_TABS = {
    funding_structure_revenue_model: 1,
    innovative_financing_mechanisms: 2,
} as const;

export const DEMAND_CAPACITY_TABS = {
    base_case_counterfactual: 1,
    demand_validation: 2,
} as const;

export const PROCUREMENT_STRATEGY_TABS = {
    procurement_strategy_type: 1,
    supplier_market_maturity: 2,
    procurement_planning: 3,
    economies_of_scale_summary: 4,
    supporting_documents: 5,
} as const;

export const GOVERNANCE_POLICY_TABS = {
    governance: 1,
    institutional_arrangements: 2,
    incentives_performance: 3,
    policy_compliance: 4,
} as const;

// Tab-wise steps, keyed by step, with every tab in display order.
export const STEP_TABS: Partial<Record<string, ReadonlyArray<StepTab>>> = {
    demand_and_capacity_validation: [
        {
            tabId: DEMAND_CAPACITY_TABS.base_case_counterfactual,
            title: 'Base Case & Counterfactual',
            section: 'dcv_base_case_counterfactual',
            fields: ['dcv_counterfactual_scenario_type', 'dcv_counterfactual_scenario_type_validation_notes', 'dcv_counterfactual_scenario_type_evidence_reference', 'dcv_current_capacity', 'dcv_current_capacity_validation_notes', 'dcv_current_capacity_evidence_reference', 'dcv_current_demand', 'dcv_current_demand_validation_notes', 'dcv_current_demand_evidence_reference', 'dcv_current_service_gap', 'dcv_current_service_gap_validation_notes', 'dcv_current_service_gap_evidence_reference', 'dcv_current_cost_of_service', 'dcv_current_cost_of_service_validation_notes', 'dcv_current_cost_of_service_evidence_reference', 'dcv_consequences_of_no_intervention', 'dcv_consequences_of_no_intervention_validation_notes', 'dcv_consequences_of_no_intervention_evidence_reference', 'dcv_base_case_confidence_level', 'dcv_base_case_confidence_level_validation_notes', 'dcv_base_case_confidence_level_evidence_reference'],
        },
        {
            tabId: DEMAND_CAPACITY_TABS.demand_validation,
            title: 'Demand Validation',
            section: 'dcv_demand_validation',
            fields: ['dcv_current_demand_units_validation_notes', 'dcv_current_demand_units_evidence_reference', 'dcv_future_demand_horizon_year', 'dcv_future_demand_horizon_year_validation_notes', 'dcv_future_demand_horizon_year_evidence_reference', 'dcv_population_served', 'dcv_population_served_validation_notes', 'dcv_population_served_evidence_reference', 'dcv_growth_rate', 'dcv_growth_rate_validation_notes', 'dcv_growth_rate_evidence_reference', 'dcv_peak_capacity_required', 'dcv_peak_capacity_required_validation_notes', 'dcv_peak_capacity_required_evidence_reference', 'dcv_demand_confidence_level', 'dcv_demand_confidence_level_validation_notes', 'dcv_demand_confidence_level_evidence_reference'],
        },
    ],
    procurement_strategy: [
        {
            tabId: PROCUREMENT_STRATEGY_TABS.procurement_strategy_type,
            title: 'Procurement Strategy Type',
            section: 'procurement_strategy_type',
            fields: ['procurement_strategy_type'],
        },
        {
            tabId: PROCUREMENT_STRATEGY_TABS.supplier_market_maturity,
            title: 'Supplier Market Maturity',
            section: 'procurement_supplier_market_maturity',
            fields: ['supplier_market_maturity', 'risk_sharing_mechanism'],
        },
        {
            tabId: PROCUREMENT_STRATEGY_TABS.procurement_planning,
            title: 'Procurement Planning',
            section: 'procurement_planning',
            fields: ['budget_statement', 'procurement_procedure', 'procurement_plan'],
        },
        {
            tabId: PROCUREMENT_STRATEGY_TABS.economies_of_scale_summary,
            title: 'Economies of Scale — Summary of Findings',
            section: 'economies_of_scale_summary_of_findings',
            fields: ['economies_of_scale_summary_of_analysis_findings'],
        },
        // Supporting documents upload from their popup, so the tab has no section.
        { tabId: PROCUREMENT_STRATEGY_TABS.supporting_documents, title: 'Supporting Documents', fields: [] },
    ],
    governance_policy_enabling_environment: [
        {
            tabId: GOVERNANCE_POLICY_TABS.governance,
            title: 'Governance',
            section: 'governance',
            fields: ['government_policies_governance_role', 'governance_structure', 'internal_controls_accountability'],
        },
        {
            tabId: GOVERNANCE_POLICY_TABS.institutional_arrangements,
            title: 'Institutional Arrangements',
            section: 'institutional_arrangements',
            fields: ['agreements_dispute_resolution'],
        },
        {
            tabId: GOVERNANCE_POLICY_TABS.incentives_performance,
            title: 'Incentives & Performance',
            section: 'incentives_performance',
            fields: ['incentives_penalties_framework'],
        },
        {
            tabId: GOVERNANCE_POLICY_TABS.policy_compliance,
            title: 'Policy & Compliance',
            section: 'policy_compliance',
            fields: ['national_policy_alignment', 'regulatory_compliance_frameworks', 'policy_alignment_summary_bfi_consistency'],
        },
    ],
    financial_and_funding_strategy_engine: [
        {
            tabId: FINANCIAL_FUNDING_TABS.funding_structure_revenue_model,
            title: 'Funding Structure & Revenue Model',
            section: 'funding_structure_revenue_model',
            fields: ['funding_sources', 'funding_secured', 'funding_pending', 'funding_gap', 'private_sector_contribution', 'public_sector_contribution', 'revenue_model'],
        },
        {
            tabId: FINANCIAL_FUNDING_TABS.innovative_financing_mechanisms,
            title: 'Innovative Financing Mechanisms',
            section: 'innovative_financing_mechanisms',
            fields: ['innovative_financing_mechanisms', 'innovative_financing_structure_and_application'],
        },
    ],
};

export type IStrategicResourcingStepTypes = IProjectFeasibilityAndOptionsAnalysis | IProjectScopeResourcesNeed
    | IFiscalSustainabilityEngine | IRiskAndSensitivityAnalysisEngine | IDemandAndCapacityValidation | ICbaCeaIntelligenceEngine | IBaseCaseAndCounterfactualScenarioEngine | IFinancialAndFundingStrategyEngine | IProcurementStrategy | IGovernancePolicy | IEconomiesOfScaleAssessment | ISpecialProcurementActionsAndSocialImpact | IDocumentData



export interface IRiskData {
    risk_id: number | null;
    risk_category_id: number;
    risk_category_name?: string;
    likelihood_key: string;
    likelihood_display_name?: string;
    likelihood_value: number;
    severity_key: string;
    severity_display_name?: string;
    severity_value: number;
    description: string;
    mitigation_strategy: string;
    risk_score: number;
    risk_level: string;
}

export interface ISummaryMatrix {
    key: string;
    label: string;
    subtitle: string;
    npv: number;
    bcr: number;
}


// The Stage 2 Core Output, calculated by the API from the saved Stage 2 fields.
export interface IDecisionData {
    checklist: IDecisionChecklistItem[];
    score_inputs: IDecisionScoreInput[];
    feasibility_score: number;
    decision: IDecision;
    decision_options: IDecisionOption[];
}

// Whether a Stage 2 step has its key evidence; key is the step's section key.
export interface IDecisionChecklistItem {
    key: string;
    done: boolean;
}

// A score (0 - 100) and its share of the feasibility score, as a percentage.
export interface IDecisionScoreInput {
    key: 'demand_validation' | 'fiscal_sustainability' | 'base_case_completeness' | 'procurement_readiness';
    value: number;
    weight: number;
}

export interface IDecision {
    key: string;
    label: string;
    status_text: string;
}

export interface IDecisionOption {
    key: string;
    icon: string;
    label: string;
    subtitle: string;
    // What happens next, shown on the active decision.
    next_step: string;
    activeClass: string;
    iconColor: string;
    is_active: boolean;
}

export interface IOptionRanking {
    rank: number;
    row_id: number;
    option_name: string;
    score: number;
    rank_label: string;
}